/**
 * AgentesPage — Página principal de Agentes e Máquinas (redesign)
 * Layout ChatGPT/Vercel com 2 tabs: Agentes / Máquinas
 * Componentes extraídos para pages/agents/
 */
import { useState, useEffect } from 'preact/hooks';
import { useTranslation } from 'react-i18next';
import { route } from 'preact-router';
import { api } from '../api/client';
import { isLoggedIn, hasActivePlan, isAdmin, authChecked, authLoading } from '../stores/auth.store';
import { showToast } from '../components/Toast';
import { PageShell, Button, Icon, SkeletonBar, TabBar } from '../components/ui';
import { BackupModal } from '../components/BackupModal';
import { LoginPage } from './Login';
import { T } from '../styles/tokens';
import { useEnvironments } from './agents/hooks';
import { AgentsList } from './agents/AgentsList';
import { MachinesList } from './agents/MachinesList';
import { CreateEnvironmentModal } from './agents/CreateEnvironmentModal';
import { ConfirmDeleteModal } from './agents/ConfirmDeleteModal';
import { LogsModal } from './agents/LogsModal';
import type { Environment, Tab } from './agents/types';

function fmtTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

export function AgentesPage() {
  // Auth guard
  if (!authChecked.value || authLoading.value) {
    return (
      <div style={{ padding: 16 }}>
        <SkeletonBar width="50%" height={14} style={{ marginBottom: 8 }} />
        <SkeletonBar width="30%" height={14} />
      </div>
    );
  }
  if (!isLoggedIn.value) return <LoginPage />;

  const { t } = useTranslation('agents');

  // Estado principal
  const {
    environments, agents, loading, userPlan, workspacesLimit,
    restartingIds, setRestartingIds, justCreated, setJustCreated,
    ready, fetchData, tokenUsage,
  } = useEnvironments();

  const [tab, setTab] = useState<Tab>('agents');
  const [showCreateEnv, setShowCreateEnv] = useState(false);
  const [showUpgradeGate, setShowUpgradeGate] = useState(false);
  const [deleteEnv, setDeleteEnv] = useState<Environment | null>(null);
  const [logsEnv, setLogsEnv] = useState<Environment | null>(null);
  const [backupModalData, setBackupModalData] = useState<{ id: string; name: string } | null>(null);

  // Listener para modais via evento customizado
  useEffect(() => {
    const backupHandler = (e: any) => setBackupModalData(e.detail);
    const logsHandler = (e: any) => setLogsEnv({ id: e.detail.id, name: e.detail.name } as Environment);
    const deleteHandler = (e: any) => setDeleteEnv({ id: e.detail.id, name: e.detail.name } as Environment);
    window.addEventListener('open-backup-modal', backupHandler);
    window.addEventListener('open-logs-modal', logsHandler);
    window.addEventListener('open-delete-modal', deleteHandler);
    return () => {
      window.removeEventListener('open-backup-modal', backupHandler);
      window.removeEventListener('open-logs-modal', logsHandler);
      window.removeEventListener('open-delete-modal', deleteHandler);
    };
  }, []);

  // Handlers
  const handleRestart = async (env: Environment) => {
    const envId = env.id || '';
    if (restartingIds.has(envId)) return;
    setRestartingIds(prev => new Set(prev).add(envId));
    try {
      const wsId = envId.replace(/^ws-/, '');
      await api.post(`/cloud/workspaces/${wsId}/restart`);
      showToast(t('restarting', { name: env.name }), 'success');
      const pollIv = setInterval(fetchData, 5000);
      setTimeout(() => {
        clearInterval(pollIv);
        setRestartingIds(prev => { const n = new Set(prev); n.delete(envId); return n; });
      }, 90000);
    } catch (e: any) {
      showToast(e.message || t('error_restart'), 'error');
      setRestartingIds(prev => { const n = new Set(prev); n.delete(envId); return n; });
    }
  };

  const handleDelete = async (env: Environment) => {
    try {
      const wsId = (env.workspace_id || env.id || '').replace(/^ws-/, '');
      console.log('[DELETE] Excluindo workspace:', wsId, 'env:', env.id, env.name);
      await api.delete(`/cloud/workspaces/${wsId}`);
      showToast(t('delete_success', { name: env.name }), 'success');
      setDeleteEnv(null);
      fetchData();
    } catch (e: any) {
      console.error('[DELETE] Erro:', e);
      showToast(e.message || t('error_delete'), 'error');
      setDeleteEnv(null);
    }
  };

  // Stats
  const totalAgents = environments.reduce((sum, env) => {
    const list = env.agents && env.agents.length > 0 ? env.agents : env.agent_info ? [env.agent_info] : [];
    return sum + (list.length || 1);
  }, 0);
  const onlineMachines = environments.filter(e => e.online).length;

  // Loading skeleton
  if (loading && environments.length === 0) {
    return (
      <div style={{ padding: 16, maxWidth: 880, margin: '0 auto' }}>
        <SkeletonBar width={180} height={28} style={{ marginBottom: 8 }} />
        <SkeletonBar width={200} height={16} style={{ marginBottom: 24 }} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {[1, 2, 3].map(i => (
            <div key={i} style={{ padding: 20, background: T.bgCard, borderRadius: 14, border: `1px solid ${T.border}`, height: 90 }}>
              <SkeletonBar width="40%" height={14} style={{ marginBottom: 8 }} />
              <SkeletonBar width="60%" height={10} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div style={{
      padding: 16, maxWidth: 880, margin: '0 auto', position: 'relative',
      boxSizing: 'border-box', opacity: ready ? 1 : 0, transition: 'opacity 0.3s ease',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
        marginBottom: 8, flexWrap: 'wrap', gap: 12,
      }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, color: T.text, margin: 0, letterSpacing: '-0.5px' }}>
          {t('title')}
        </h1>
        <Button variant="primary" size="md" onClick={() => setShowCreateEnv(true)}
          icon={<Icon name="plus" size={16} />}
          style={{ borderRadius: 10, padding: '10px 20px', fontSize: 14, fontWeight: 600 }}
        >
          {t('new_agent')}
        </Button>
      </div>
      <div style={{ fontSize: 13, color: T.textSecondary, marginBottom: 24 }}>
        {t('agents_count', { count: totalAgents })} · {t('online_count', { count: onlineMachines })}
      </div>

      {/* Tabs */}
      <TabBar
        tabs={[
          { id: 'agents', label: t('tab_agents') },
          { id: 'machines', label: t('tab_machines') },
        ]}
        active={tab}
        onChange={(id) => setTab(id as Tab)}
      />

      {/* Conteúdo das tabs */}
      {tab === 'agents' && (
        <AgentsList
          environments={environments}
          userPlan={userPlan}
          loading={loading}
          onCreateAgent={() => setShowCreateEnv(true)}
          tokenUsage={tokenUsage}
        />
      )}

      {tab === 'machines' && (
        <MachinesList
          environments={environments}
          userPlan={userPlan}
          loading={loading}
          justCreated={justCreated}
          restartingIds={restartingIds}
          onCreateEnv={() => setShowCreateEnv(true)}
          onRestart={handleRestart}
          onRefresh={fetchData}
          onLogs={(env) => setLogsEnv(env)}
          onDelete={(env) => setDeleteEnv(env)}
        />
      )}

      {/* Modais */}
      {showCreateEnv && (
        <CreateEnvironmentModal
          environments={environments}
          onClose={() => setShowCreateEnv(false)}
          onSuccess={() => { setShowCreateEnv(false); setJustCreated(true); setTimeout(fetchData, 3000); }}
          onQuotaExceeded={() => setShowUpgradeGate(true)}
          currentEnvCount={environments.filter(e => e.status !== 'deleted').length}
          maxEnvAllowed={workspacesLimit}
        />
      )}

      {deleteEnv && (
        <ConfirmDeleteModal
          name={deleteEnv.name}
          onConfirm={() => handleDelete(deleteEnv)}
          onClose={() => setDeleteEnv(null)}
        />
      )}

      {logsEnv && (
        <LogsModal
          envId={logsEnv.id}
          envName={logsEnv.name}
          onClose={() => setLogsEnv(null)}
        />
      )}

      {backupModalData && (
        <BackupModal
          workspaceId={backupModalData.id}
          workspaceName={backupModalData.name}
          onClose={() => setBackupModalData(null)}
        />
      )}

      {/* Animações globais */}
      <style>{`
        @keyframes statusPulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </div>
  );
}
